<script lang="ts">
export const elInlineBannerStatuses = ['error', 'warning', 'info', 'laiana'] as const;
export type ElInlineBannerStatuses = (typeof elInlineBannerStatuses)[number];
</script>

<script setup lang="ts">
import { computed, type VNode } from 'vue';
import ElIcon from '@/ElIcon.vue';
import ElButton from '@/ElButton.vue';
import ElSpinner from '@/ElSpinner.vue';
import laianaIcon from '@/assets/laiana/laiana.png';

const props = withDefaults(
  defineProps<{
    status?: ElInlineBannerStatuses;
    title?: string | VNode;
    description?: string;
    loading?: boolean;
    action?: Pick<
      InstanceType<typeof ElButton>['$props'],
      'label' | 'variant' | 'onClick' | 'icon' | 'iconRight' | 'loading' | 'loadOnClick'
    >;
    icon?: InstanceType<typeof ElIcon>['$props'];
  }>(),
  {
    status: 'info',
    title: undefined,
    description: undefined,
    action: undefined,
    icon: undefined,
  },
);

const iconName = computed(() => {
  if (props.icon?.name) return props.icon.name;

  switch (props.status) {
    case 'info':
      return 'InformationCircleIcon';
    case 'warning':
      return 'ExclamationTriangleIcon';
    case 'error':
      return 'InformationCircleIcon';
    default:
      return 'InformationCircleIcon';
  }
});
</script>

<template>
  <div
    class="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-4 w-full min-w-72 p-4 rounded-md"
    :class="{
      'bg-brandBlue-50 text-brandBlue-800': status === 'info',
      'bg-yellow-50 text-yellow-700': status === 'warning',
      'bg-red-50 text-red-700': status === 'error',
      'text-white': status === 'laiana',
    }"
    :style="status === 'laiana' ? { background: 'linear-gradient(to right, #AB5AE2, #A5B5F6)' } : {}"
  >
    <div class="flex flex-row items-start gap-x-2 flex-grow basis-64">
      <ElSpinner v-if="loading" class="flex-shrink-0 px-1 pt-1" />
      <img v-else-if="status === 'laiana'" :src="laianaIcon" alt="Laiana icon" class="w-6 h-6 flex-shrink-0" />
      <ElIcon
        v-else
        v-bind="icon"
        :name="iconName"
        class="w-6 h-6 flex-shrink-0"
        :class="{
          'text-brandBlue-500': status === 'info',
          'text-yellow-500': status === 'warning',
          'text-red-500': status === 'error',
        }"
      />
      <div class="text-sm flex flex-col flex-grow mt-0.5">
        <component :is="typeof title === 'string' ? 'span' : title" v-if="title" class="font-semibold min-h-6">
          {{ typeof title === 'string' ? title : '' }}
        </component>
        <span v-if="$slots.description || description">
          <slot name="description">{{ description }}</slot>
        </span>
      </div>
    </div>
    <ElButton
      v-if="action"
      v-bind="action"
      :variant="action.variant ?? 'tertiary'"
      class="flex-shrink-0"
      :class="{
        underline: !action.variant || action.variant === 'tertiary',
        'text-red-700 hover:text-red-500 hover:bg-transparent active:text-red-500 active:bg-red-100': status === 'error',
        'text-yellow-700 hover:text-yellow-500 hover:bg-transparent active:text-yellow-500 active:bg-yellow-100': status === 'warning',
        'text-brandBlue-800 hover:text-brandBlue-500 hover:bg-transparent': status === 'info',

        'text-gray-900 hover:bg-gray-50 active:bg-gray-100': status === 'laiana',
      }"
      :label="action?.label"
      @click="action?.onClick"
    />
  </div>
</template>
